import { GeoLocationUtil } from './utils/geolocation.js';
import { SecurityUtil } from './utils/security.js';
import { StorageUtil } from './utils/storage.js';
import { ApiUtil } from './utils/api.js';

/**
 * Casino Audit SDK for geo-compliance and fraud detection
 * @class CasinoAuditSDK
 */
class CasinoAuditSDK {
  constructor(config = {}) {
    this.version = '1.0.0';
    this.config = {
      siteId: config.siteId,
      apiKey: config.apiKey,
      auditEndpoint: "http://localhost:5000",
      enableRealTimeMonitoring: config.enableRealTimeMonitoring || false,
      reportingInterval: config.reportingInterval || 300000, // 5 minutes
      debug: config.debug || false,
      retryAttempts: config.retryAttempts || 3,
      timeout: config.timeout || 30000,
      ...config
    };

    // Initialize utilities
    this.geo = new GeoLocationUtil(this.config);
    this.security = new SecurityUtil(this.config);
    this.storage = new StorageUtil(this.config);
    this.api = new ApiUtil(this.config);

    // Event listeners
    this.listeners = {};

    // Internal state
    this.isInitialized = false;
    this.monitoringActive = false;
    this.lastLocationCheck = null;
    this.sessionStartTime = Date.now();

    // Initialize
    this.init();
  }

  /**
   * Initialize the SDK
   */
  async init() {
    try {
      this.log('Initializing Casino Audit SDK v' + this.version);
      
      // Validate required config
      if (!this.config.siteId || !this.config.apiKey) {
        throw new Error('siteId and apiKey are required');
      }

      // Store session info
      this.storage.set('sessionStartTime', this.sessionStartTime);
      this.storage.increment('pageViews');

      // Start monitoring if enabled
      if (this.config.enableRealTimeMonitoring) {
        await this.startMonitoring();
      }

      this.isInitialized = true;
      this.emit('initialized', { timestamp: new Date().toISOString() });
      
      this.log('SDK initialized successfully');
    } catch (error) {
      this.error('SDK initialization failed:', error);
      throw error;
    }
  }

  /**
   * Check geo-compliance for current user
   * @returns {Promise<Object>} Compliance result
   */
  async checkGeoCompliance() {
    try {
      this.log('Checking geo-compliance...');
      
      const userLocation = await this.geo.getUserLocation();
      const complianceData = {
        siteId: this.config.siteId,
        userLocation,
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent,
        referrer: document.referrer,
        currentPage: window.location.href,
        sessionDuration: this.getSessionDuration(),
        pageViews: this.storage.get('pageViews') || 1
      };

      const result = await this.api.post('/geo-compliance', complianceData);
      
      this.lastLocationCheck = result;
      this.storage.set('lastComplianceCheck', result);
      
      this.emit('complianceCheck', result);
      
      if (!result.allowed) {
        this.emit('restricted', result);
        this.handleRestriction(result);
      }

      return result;
    } catch (error) {
      this.error('Geo-compliance check failed:', error);
      return this.handleComplianceError(error);
    }
  }

  /**
   * Detect proxy or VPN usage
   * @returns {Promise<Object>} Security analysis result
   */
  async detectProxyOrVPN() {
    try {
      this.log('Detecting proxy/VPN usage...');
      
      const indicators = await this.security.analyzeConnection();
      const result = await this.api.post('/security-check', {
        siteId: this.config.siteId,
        indicators,
        timestamp: new Date().toISOString()
      });

      if (result.riskScore > 70) {
        this.emit('highRisk', result);
      }

      return result;
    } catch (error) {
      this.error('Security check failed:', error);
      return { riskScore: 0, indicators: [], error: error.message };
    }
  }

  /**
   * Start real-time monitoring
   */
  async startMonitoring() {
    if (this.monitoringActive) {
      this.log('Monitoring already active');
      return;
    }

    this.log('Starting real-time monitoring...');
    this.monitoringActive = true;

    // Initial compliance check
    await this.checkGeoCompliance();

    // Set up periodic monitoring
    this.monitoringInterval = setInterval(async () => {
      try {
        const auditData = await this.collectAuditData();
        await this.api.post('/monitoring', auditData);
      } catch (error) {
        this.error('Monitoring cycle failed:', error);
      }
    }, this.config.reportingInterval);

    // Set up suspicious activity detection
    this.setupSuspiciousActivityDetection();

    this.emit('monitoringStarted', { timestamp: new Date().toISOString() });
  }

  /**
   * Stop real-time monitoring
   */
  stopMonitoring() {
    if (!this.monitoringActive) return;

    this.log('Stopping monitoring...');
    
    if (this.monitoringInterval) {
      clearInterval(this.monitoringInterval);
    }

    if (this.activityInterval) {
      clearInterval(this.activityInterval);
    }

    this.monitoringActive = false;
    this.emit('monitoringStopped', { timestamp: new Date().toISOString() });
  }

  /**
   * Set up suspicious activity detection
   */
  setupSuspiciousActivityDetection() {
    let locationChangeCount = 0;
    let lastLocation = null;
    let rapidActionCount = 0;

    // Monitor location changes
    this.activityInterval = setInterval(async () => {
      try {
        const currentLocation = await this.geo.getUserLocation();
        
        if (lastLocation && this.geo.hasLocationChanged(lastLocation, currentLocation)) {
          locationChangeCount++;
          
          if (locationChangeCount > 3) {
            await this.reportSuspiciousActivity('rapid_location_change', {
              changes: locationChangeCount,
              currentLocation,
              lastLocation,
              timeWindow: '60_seconds'
            });
          }
        }
        
        lastLocation = currentLocation;
        
        // Reset counter every hour
        if (Date.now() % 3600000 < 60000) {
          locationChangeCount = 0;
        }
      } catch (error) {
        this.error('Location monitoring failed:', error);
      }
    }, 60000); // Check every minute

    // Monitor rapid actions
    let lastActionTime = 0;
    const originalFetch = window.fetch;
    
    window.fetch = (...args) => {
      const now = Date.now();
      if (now - lastActionTime < 100) { // Actions within 100ms
        rapidActionCount++;
        if (rapidActionCount > 10) {
          this.reportSuspiciousActivity('rapid_actions', {
            count: rapidActionCount,
            timeWindow: '100ms'
          });
        }
      } else {
        rapidActionCount = 0;
      }
      lastActionTime = now;
      
      return originalFetch.apply(this, args);
    };
  }

  /**
   * Report suspicious activity
   */
  async reportSuspiciousActivity(type, data) {
    try {
      const activity = {
        siteId: this.config.siteId,
        type,
        data,
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent,
        currentPage: window.location.href
      };

      const result = await this.api.post('/suspicious-activity', activity);
      
      this.emit('suspiciousActivity', { type, data, result });
      
      if (result.action === 'block') {
        this.handleAutoBlock(result);
      }

      return result;
    } catch (error) {
      this.error('Failed to report suspicious activity:', error);
    }
  }

  /**
   * Collect comprehensive audit data
   */
  async collectAuditData() {
    const data = {
      siteId: this.config.siteId,
      timestamp: new Date().toISOString(),
      session: {
        duration: this.getSessionDuration(),
        pageViews: this.storage.get('pageViews') || 1,
        actions: this.storage.get('recentActions') || []
      },
      technical: {
        userAgent: navigator.userAgent,
        screenResolution: `${screen.width}x${screen.height}`,
        language: navigator.language,
        platform: navigator.platform,
        cookieEnabled: navigator.cookieEnabled,
        onLine: navigator.onLine,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
      },
      location: await this.geo.getUserLocation(),
      security: await this.security.analyzeConnection(),
      performance: {
        memoryUsage: performance.memory ? {
          used: performance.memory.usedJSHeapSize,
          total: performance.memory.totalJSHeapSize,
          limit: performance.memory.jsHeapSizeLimit
        } : null,
        connectionType: navigator.connection ? navigator.connection.effectiveType : null
      }
    };

    return data;
  }

  /**
   * Handle geo-restriction
   */
  handleRestriction(result) {
    this.log('Handling geo-restriction:', result);
    
    // Store restriction info
    this.storage.set('restrictionInfo', result);
    
    // Trigger custom restriction handler if provided
    if (typeof this.config.onRestricted === 'function') {
      this.config.onRestricted(result);
    }
  }

  /**
   * Handle auto-block scenario
   */
  handleAutoBlock(result) {
    this.log('Handling auto-block:', result);
    
    this.storage.set('blockInfo', result);
    this.emit('autoBlocked', result);
    
    if (typeof this.config.onBlocked === 'function') {
      this.config.onBlocked(result);
    }
  }

  /**
   * Handle compliance check errors
   */
  handleComplianceError(error) {
    const fallbackResult = {
      allowed: true, // Fail open for better UX
      country: 'Unknown',
      riskScore: 50,
      error: error.message,
      fallback: true
    };

    this.storage.set('lastComplianceError', {
      error: error.message,
      timestamp: new Date().toISOString()
    });

    return fallbackResult;
  }

  /**
   * Get session duration
   */
  getSessionDuration() {
    return Date.now() - this.sessionStartTime;
  }

  /**
   * Event system
   */
  on(event, callback) {
    if (!this.listeners[event]) {
      this.listeners[event] = [];
    }
    this.listeners[event].push(callback);
  }

  off(event, callback) {
    if (!this.listeners[event]) return;
    
    const index = this.listeners[event].indexOf(callback);
    if (index > -1) {
      this.listeners[event].splice(index, 1);
    }
  }

  emit(event, data) {
    if (!this.listeners[event]) return;
    
    this.listeners[event].forEach(callback => {
      try {
        callback(data);
      } catch (error) {
        this.error('Event callback failed:', error);
      }
    });
  }

  /**
   * Logging utilities
   */
  log(...args) {
    if (this.config.debug) {
      console.log('[CasinoAudit]', ...args);
    }
  }

  warn(...args) {
    if (this.config.debug) {
      console.warn('[CasinoAudit]', ...args);
    }
  }

  error(...args) {
    if (this.config.debug) {
      console.error('[CasinoAudit]', ...args);
    }
  }

  /**
   * Get SDK info
   */
  getInfo() {
    return {
      version: this.version,
      config: { ...this.config, apiKey: '[HIDDEN]' },
      isInitialized: this.isInitialized,
      monitoringActive: this.monitoringActive,
      sessionDuration: this.getSessionDuration(),
      lastLocationCheck: this.lastLocationCheck
    };
  }

  /**
   * Cleanup
   */
  destroy() {
    this.stopMonitoring();
    this.listeners = {};
    this.isInitialized = false;
    this.log('SDK destroyed');
  }
}

// Auto-initialize if config is available
if (typeof window !== 'undefined' && window.CasinoAuditConfig) {
  window.CasinoAudit = new CasinoAuditSDK(window.CasinoAuditConfig);
}

export default CasinoAuditSDK;